import {
    Button,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import {useStyles} from "@/components/call-group/members/styles";
import {useState} from "react";
import {
    DialogActions,
    DialogContent,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../../CreateAnotherCheckBox";
import ErrorHelper from "../../../utils/ErrorHelper";
import MultipleLineError from "../../MultipleLineError";
import {
    useGetRandomPasswordQuery,
    useExtensionsQuery,
    useFaxesEmailsQuery,
    ExtensionsQueryHookResult,
    GetRandomPasswordQueryHookResult,
    FaxesEmailsQueryHookResult,
} from "@/components/fax/queries.generated";
import {ShowPasswordIcon, HidePasswordIcon, OwnerIcon} from "@/components/icons";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import OutgoingCallBarringWidget from "@/outgoing-call-barring/OutgoingCallBarringWidget";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Loading from "@/components/breadcrumbs/Loading";
import {useDomainNameQuery} from "@/queries.generated";
import {DomainOutgoingCallBarrings, useDomainOutgoingCallBarringsQuery} from "@/hooks";
import EndpointCreateDialog, {QuotaInfo} from "@/components/EndpointCreateDialog";
import {CreateFaxForm} from "./form";

export type Props = {
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    isOpen: boolean;
    quotaInfo?: QuotaInfo;
    title: MessageDescriptor;
    successItemPath: string;
};

type ContentProps = {
    randomPasswordQuery: GetRandomPasswordQueryHookResult;
    extensionsQuery: ExtensionsQueryHookResult;
    faxesEmailsQuery: FaxesEmailsQueryHookResult;
    domainCallBarrings: DomainOutgoingCallBarrings;
    domainName: string;
} & Props;

const Content = ({
    domainId,
    domainName,
    onSuccess,
    isOpen,
    close,
    quotaInfo,
    title,
    successItemPath,
    randomPasswordQuery,
    extensionsQuery,
    faxesEmailsQuery,
    domainCallBarrings,
}: ContentProps) => {
    const form = new CreateFaxForm(domainId, domainName, randomPasswordQuery.data.sipUser.password, successItemPath);

    const domainContextFilter = ContextFilters.domain(domainName);
    const auth = useAuth();

    const canEdit = auth.hasPermission("edit_fax", domainContextFilter);
    const canEditOperator = auth.hasPermission("edit_fax_superuser", domainContextFilter);

    const [showPassword, setShowPassword] = useState(false);
    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);
    const [selectedAdditionalEmail, setAdditionalEmail] = useState("0");
    const {classes, cx} = useStyles();

    const faxesEmails = faxesEmailsQuery.data
        ? faxesEmailsQuery.data?.faxesEmails.items.map((faxesEmail) => faxesEmail.extensionId)
        : [];

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    function handleClose() {
        close();
        form.reset();
    }

    async function submit() {
        if (!(await form.submit())) return;
        onSuccess();

        if (createAnother) form.reset();
        else handleClose();
    }

    function addEmail(selectedEmail: string) {
        const newData = {
            ...form.values,
            ...{additionalFaxSendingEmails: [...form.values.additionalFaxSendingEmails, selectedEmail]},
        };
        form.updateValues(newData);
        setAdditionalEmail("0");
    }

    function removeEmail(index: number) {
        const newData = {
            ...form.values,
            ...{
                additionalFaxSendingEmails: [
                    ...[...form.values.additionalFaxSendingEmails].filter((_, i) => i !== index),
                ],
            },
        };
        form.updateValues(newData);
    }

    const generateRandomPassword = async () => {
        const {data} = await randomPasswordQuery.refetch();
        const newPassword = data.sipUser.password;
        form.updateValues({password: newPassword});
    };

    return (
        <EndpointCreateDialog
            title={title}
            isOpen={isOpen}
            closeDialog={close}
            quotaInfo={quotaInfo}
            loading={quotaInfo === undefined}
        >
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow label={defineMessage({id: "e164", defaultMessage: "E.164"})}>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.e164}
                            onChange={(event) => form.updateValues({e164: event.target.value})}
                            error={errorHelper.hasError("e164")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("e164")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "identifier", defaultMessage: "Identifier"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.shortNumber}
                            onChange={(event) => form.updateValues({shortNumber: event.target.value})}
                            error={errorHelper.hasError("shortNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("shortNumber")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                        labelAlign="top"
                    >
                        <OutgoingCallBarringWidget
                            masterBarrings={domainCallBarrings}
                            operatorBarrings={form.values.outgoingCallBarringOperator}
                            adminBarrings={form.values.outgoingCallBarringAdmin}
                            updateOperatorBarrings={(barrings) =>
                                form.updateValues({outgoingCallBarringOperator: barrings})
                            }
                            updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                            disabledAdmin={!canEdit}
                            disabledOperator={!canEditOperator}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "fax_receiving_email_address",
                            defaultMessage: "Fax receiving email address",
                        })}
                        required
                    >
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.incomingEmailAddress}
                            onChange={(event) => form.updateValues({incomingEmailAddress: event.target.value})}
                            error={errorHelper.hasError("incomingEmailAddress")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("incomingEmailAddress")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "fax_sending_email_address",
                            defaultMessage: "Fax sending email address",
                        })}
                    >
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.senderEmailAddress}
                            onChange={(event) => form.updateValues({senderEmailAddress: event.target.value})}
                            error={errorHelper.hasError("senderEmailAddress")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("senderEmailAddress")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "fax_sending_password", defaultMessage: "Fax sending password"})}
                        required
                    >
                        <TextField
                            variant="standard"
                            type={showPassword ? "text" : "password"}
                            margin="dense"
                            value={form.values.password}
                            onChange={(event) => form.updateValues({password: event.target.value})}
                            error={errorHelper.hasError("password")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("password")} />}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="Toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            size="large"
                                        >
                                            {showPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                                        </IconButton>
                                        <IconButton
                                            aria-label="Generate new password"
                                            onClick={generateRandomPassword}
                                            size="large"
                                        >
                                            <OwnerIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "notify_about_receiving",
                            defaultMessage: "Notify about unsuccessful receiving",
                        })}
                    >
                        <Checkbox
                            value={form.values.notifyUnsuccessfulReceiving}
                            checked={form.values.notifyUnsuccessfulReceiving}
                            onChange={(event) => form.updateValues({notifyUnsuccessfulReceiving: event.target.checked})}
                            color="primary"
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "additional_fax_sending_emails",
                            defaultMessage: "Additional fax sending email addresses",
                        })}
                    >
                        <Grid container size={12}>
                            <Grid size={12}>
                                {form.values.additionalFaxSendingEmails.length ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow className={cx(classes.row, classes.header)}>
                                                <TableCell>
                                                    <FormattedMessage id="shortNumber" defaultMessage="Short number" />
                                                </TableCell>
                                                <TableCell>
                                                    <FormattedMessage id="displayName" defaultMessage="Name" />
                                                </TableCell>
                                                <TableCell>
                                                    <FormattedMessage id="email" defaultMessage="Email" />
                                                </TableCell>
                                                <TableCell>
                                                    <FormattedMessage id="action" defaultMessage="Action" />
                                                </TableCell>
                                            </TableRow>
                                            {extensionsQuery.data.users.items
                                                .filter((ext) =>
                                                    form.values.additionalFaxSendingEmails.includes(
                                                        ext.number.shortNumber,
                                                    ),
                                                )
                                                .map((ext, index) => (
                                                    <TableRow key={index} className={classes.row}>
                                                        <TableCell>{ext.number.shortNumber}</TableCell>
                                                        <TableCell>{ext.displayName}</TableCell>
                                                        <TableCell>{ext.notificationEmail}</TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => removeEmail(index)}
                                                            >
                                                                <FontAwesomeIcon icon="trash" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableHead>
                                    </Table>
                                ) : null}
                            </Grid>
                            <Grid style={{marginTop: "20px"}} size={12}>
                                <div style={{display: "flex"}}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        style={{marginRight: "25px"}}
                                        onClick={() => addEmail(selectedAdditionalEmail)}
                                        disabled={
                                            form.values.additionalFaxSendingEmails.includes(selectedAdditionalEmail) ||
                                            selectedAdditionalEmail === "0"
                                        }
                                    >
                                        <FormattedMessage id="add" defaultMessage="Add" />
                                    </Button>
                                    <Select
                                        variant="standard"
                                        fullWidth
                                        margin="dense"
                                        value={selectedAdditionalEmail}
                                        onChange={(event) => setAdditionalEmail(event.target.value.toString())}
                                    >
                                        <MenuItem key={0} value={0}>
                                            <FormattedMessage id="select" defaultMessage="Select" />
                                        </MenuItem>
                                        {extensionsQuery.data?.users.items
                                            .filter((ext) => ext.notificationEmail && !faxesEmails.includes(ext.id))
                                            .map((ext) => (
                                                <MenuItem
                                                    key={ext.number.shortNumber}
                                                    value={ext.number.shortNumber}
                                                    disabled={form.values.additionalFaxSendingEmails.includes(
                                                        ext.number.shortNumber,
                                                    )}
                                                >
                                                    {ext.number.shortNumber} - {ext.displayName} -{" "}
                                                    {ext.notificationEmail}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </div>
                            </Grid>
                        </Grid>
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={close}
                    mainButtonEnabled={form.hasChanged}
                    secondaryButtonEnabled={form.hasChanged}
                />
            </DialogActions>
        </EndpointCreateDialog>
    );
};

export default ({domainId, onSuccess, isOpen, close, quotaInfo, title, successItemPath}: Props) => {
    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
    });

    const randomPasswordQuery = useGetRandomPasswordQuery({
        fetchPolicy: "no-cache",
    });

    const extensionsQuery = useExtensionsQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const faxesEmailsQuery = useFaxesEmailsQuery({
        fetchPolicy: "no-cache",
    });

    const domainCallBarringsQuery = useDomainOutgoingCallBarringsQuery(domainId);

    if (
        extensionsQuery.loading ||
        faxesEmailsQuery.loading ||
        randomPasswordQuery.loading ||
        domainNameQuery.loading ||
        domainCallBarringsQuery.loading
    ) {
        return <Loading />;
    }

    return (
        <Content
            domainId={domainId}
            domainName={domainNameQuery.data?.domains.items[0].name}
            onSuccess={onSuccess}
            isOpen={isOpen}
            close={close}
            quotaInfo={quotaInfo}
            title={title}
            successItemPath={successItemPath}
            extensionsQuery={extensionsQuery}
            faxesEmailsQuery={faxesEmailsQuery}
            domainCallBarrings={domainCallBarringsQuery.domainOutgoingCallBarrings}
            randomPasswordQuery={randomPasswordQuery}
        />
    );
};
